import { useI18n } from '@upweo/ui';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@upweo/ui';
import type { FinancialStatus, FulfillmentStatus, OrderDetail } from '@upweo/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import * as api from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { PageHeader } from './PageHeader.js';

type Tone = 'success' | 'warning' | 'info' | 'default';

/**
 * Status -> tone, not status -> colour. A theme change cannot leave "green"
 * meaning "failed", and the mapping is the single place to reason about which
 * states a merchant should actually notice.
 */
const FINANCIAL_TONE: Record<FinancialStatus, Tone> = {
  PAID: 'success',
  PENDING: 'warning',
  AUTHORIZED: 'info',
  PARTIALLY_REFUNDED: 'warning',
  REFUNDED: 'default',
  VOIDED: 'default',
};

const FULFILMENT_TONE: Record<FulfillmentStatus, Tone> = {
  FULFILLED: 'success',
  UNFULFILLED: 'default',
  PARTIAL: 'warning',
  RESTOCKED: 'default',
};

/** Enum -> sentence case, so UNFULFILLED does not shout from every row. */
const Pill = ({ label, tone }: { label: string; tone: Tone }): React.JSX.Element => (
  <Badge variant={tone}>{label.toLowerCase().replace(/_/g, ' ')}</Badge>
);

const OrderDrawer = ({ id, onClose }: { id: string; onClose: () => void }): React.JSX.Element => {
  const { t } = useI18n();
  const { data, isPending, isError } = useQuery<OrderDetail>({
    queryKey: ['order', id],
    queryFn: () => api.getOrder(id),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>{data ? `Order ${data.name}` : 'Order'}</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </CardHeader>

      <CardContent className="space-y-5">
        {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
        {isError && <ErrorState message="Could not load this order." />}

        {data && (
          <>
            <div className="grid gap-4 text-sm sm:grid-cols-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Customer
                </div>
                <div className="mt-0.5">{data.customerName ?? data.email ?? '—'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('orders.columnPlaced')}
                </div>
                <div className="mt-0.5">{api.formatDate(data.createdAt)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('common.status')}
                </div>
                <div className="mt-1 flex gap-1.5">
                  <Pill
                    label={t(`orders.financial.${data.financialStatus}`)}
                    tone={FINANCIAL_TONE[data.financialStatus]}
                  />
                  <Pill
                    label={t(`orders.fulfilment.${data.fulfillmentStatus}`)}
                    tone={FULFILMENT_TONE[data.fulfillmentStatus]}
                  />
                </div>
              </div>
            </div>

            <TableWrap className="shadow-none">
              <Table>
                <THead>
                  <TR className="hover:bg-transparent">
                    <TH>{t('orders.item')}</TH>
                    <TH className="text-right">{t('orders.qty')}</TH>
                    <TH className="text-right">{t('orders.unit')}</TH>
                    <TH className="text-right">{t('common.total')}</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.lineItems.map((li) => (
                    <TR key={li.id} className="hover:bg-transparent">
                      <TD>
                        <div>{li.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {[li.variantTitle, li.sku].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </TD>
                      <TD className="tabular text-right">{li.quantity}</TD>
                      <TD className="tabular text-right">
                        {api.formatMoney(li.unitPrice, data.currencyCode)}
                      </TD>
                      <TD className="tabular text-right">
                        {api.formatMoney(li.totalPrice, data.currencyCode)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>

            <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
              {(
                [
                  ['Subtotal', data.subtotalPrice],
                  ['Discounts', data.totalDiscount],
                  ['Shipping', data.totalShipping],
                  ['Tax', data.totalTax],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="flex justify-between text-muted-foreground">
                  <span>{label}</span>
                  <span className="tabular">{api.formatMoney(value, data.currencyCode)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 font-medium">
                <span>{t('common.total')}</span>
                <span className="tabular">
                  {api.formatMoney(data.totalPrice, data.currencyCode)}
                </span>
              </div>
            </div>

            {data.shippingAddress && (
              <div className="text-sm">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('orders.shipTo')}
                </div>
                <div className="mt-1 text-muted-foreground">
                  {[
                    [data.shippingAddress.firstName, data.shippingAddress.lastName]
                      .filter(Boolean)
                      .join(' '),
                    data.shippingAddress.address1,
                    data.shippingAddress.city,
                    data.shippingAddress.postalCode,
                    data.shippingAddress.countryCode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const OrdersPage = (): React.JSX.Element => {
  const { t } = useI18n();
  const { activeStore } = useAuth();
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  // activeStore is part of the key so switching stores cannot serve the
  // previous tenant's orders from cache.
  const ordersQuery = useQuery({
    queryKey: ['orders', activeStore?.tenantId, search],
    queryFn: ({ signal }) => api.listOrders({ search, signal }),
    enabled: Boolean(activeStore),
  });

  const orders = ordersQuery.data?.items ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('orders.title')}
        subtitle={activeStore ? `${orders.length} in ${activeStore.tenantName}` : 'No store'}
      />

      <Input
        type="search"
        placeholder={t('orders.searchPlaceholder')}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        className="max-w-sm"
      />

      {openId && (
        <OrderDrawer
          id={openId}
          onClose={() => {
            setOpenId(null);
          }}
        />
      )}

      <TableWrap>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>{t('orders.columnOrder')}</TH>
              <TH>{t('orders.columnCustomer')}</TH>
              <TH>{t('orders.columnPayment')}</TH>
              <TH>{t('orders.columnFulfilment')}</TH>
              <TH className="text-right">Items</TH>
              <TH>{t('orders.columnPlaced')}</TH>
              <TH className="text-right">{t('common.total')}</TH>
            </TR>
          </THead>
          <TBody>
            {ordersQuery.isPending && <SkeletonRows rows={4} cols={7} />}

            {ordersQuery.isError && (
              <tr>
                <td colSpan={7}>
                  <ErrorState message="Failed to load orders" />
                </td>
              </tr>
            )}

            {!ordersQuery.isPending && !ordersQuery.isError && orders.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    title={search ? 'No matching orders' : 'No orders yet'}
                    description={
                      search
                        ? 'Try a different search term.'
                        : 'Orders placed through the storefront appear here.'
                    }
                  />
                </td>
              </tr>
            )}

            {orders.map((o) => (
              <TR
                key={o.id}
                onClick={() => {
                  setOpenId(o.id);
                }}
                className="cursor-pointer"
              >
                <TD className="font-medium">{o.name}</TD>
                <TD>{o.customerName ?? o.email ?? '—'}</TD>
                <TD>
                  <Pill
                    label={t(`orders.financial.${o.financialStatus}`)}
                    tone={FINANCIAL_TONE[o.financialStatus]}
                  />
                </TD>
                <TD>
                  <Pill
                    label={t(`orders.fulfilment.${o.fulfillmentStatus}`)}
                    tone={FULFILMENT_TONE[o.fulfillmentStatus]}
                  />
                </TD>
                <TD className="tabular text-right text-muted-foreground">{o.lineItemCount}</TD>
                <TD className="text-muted-foreground">{api.formatDate(o.createdAt)}</TD>
                <TD className="tabular text-right">
                  {api.formatMoney(o.totalPrice, o.currencyCode)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
    </div>
  );
};
